import ChartDataTable from '@/pages/CompanyReport/components/Overview/components/ChartDataTable';
import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { TipContainer } from '../KeyMetricsChart/styled';
import Tooltip from '@/components/Tooltip';
import { TooltipName, TableContainer } from './styled';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import {
  useBaseMetricLabel,
  useCalculatedBaseMetricLabel,
  useMetricRatioLabel,
} from '../../hooks';
import { useMetricFullLabel } from '../../../../hooks/useLabel';

const isMetric = (
  metric: string
): metric is 'PB' | 'PS' | 'Forward PS' | 'PE' | 'Forward PE' => {
  return ['PB', 'PS', 'Forward PS', 'PE', 'Forward PE'].includes(metric);
};

interface KeyMetricsTableProps {
  metric: string;
  value: string;
}

export const KeyMetricsTable = ({
  metric: metricProp,
  value,
}: KeyMetricsTableProps) => {
  const metric = isMetric(metricProp) ? metricProp : 'n/a';

  const { formattedMarketCap, metricsData, tickerSymbol } = useValuationData();

  const getTerm = useGlossaryTerms();

  const getBaseMetricLabel = useBaseMetricLabel();
  const getBaseMetricCalculatedLabel = useCalculatedBaseMetricLabel();
  const getMetricRatioLabel = useMetricRatioLabel();
  const getMetricFullLabel = useMetricFullLabel();

  const t = useValuationTranslation();

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: t('keyMetric.table.heading', {
            tickerSymbol,
            metric,
          }),
          about: t('keyMetric.table.description', {
            tickerSymbol,
            metricLabel: getMetricFullLabel(metric),
            baseMetric: getBaseMetricCalculatedLabel(
              metricsData[metric]?.label
            ),
          }),
        }}
      />
      <TableContainer>
        <ChartDataTable data-cy-id="key-metrics-table" columnTextAlignments="l">
          <caption>
            {t('keyMetric.table.description', {
              tickerSymbol,
              metricLabel: getMetricFullLabel(metric),
              baseMetric: getBaseMetricCalculatedLabel(
                metricsData[metric]?.label
              ),
            })}
          </caption>
          <thead>
            <tr>
              <th colSpan={2}>
                {t('keyMetric.table.heading', {
                  tickerSymbol,
                  metric,
                })}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Tooltip
                  openDelay={300}
                  light
                  title={
                    <TipContainer>
                      {getTerm(
                        `${
                          metric.toLowerCase() as Lowercase<typeof metric>
                        } ratio` as const
                      )}
                    </TipContainer>
                  }
                >
                  <TooltipName>{getMetricRatioLabel(metric)}</TooltipName>
                </Tooltip>
              </td>
              <td>{value}</td>
            </tr>
            <tr>
              <td>{getBaseMetricLabel(metricsData[metric]?.label) || 'n/a'}</td>
              <td>{metricsData[metric]?.formattedValue || 'n/a'}</td>
            </tr>
            <tr>
              <td>{t('keyMetric.marketCap')}</td>
              <td>{formattedMarketCap}</td>
            </tr>
          </tbody>
        </ChartDataTable>
      </TableContainer>
    </>
  );
};
